import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { SCORECARDS } from "@/lib/scorecard";

const N = 7;
const C = 180;
const R = 100;
const angle = (i) => -Math.PI / 2 + (i * 2 * Math.PI) / N;
const point = (i, r) => [C + Math.cos(angle(i)) * r, C + Math.sin(angle(i)) * r];
const path = (values) =>
  values
    .map((v, i) => {
      const [x, y] = point(i, (R * (v ?? 0)) / 3);
      return `${i ? "L" : "M"}${x.toFixed(1)} ${y.toFixed(1)}`;
    })
    .join("") + "Z";
const ring = (level) => path(Array(N).fill(level));

// Decorative shapes the intro cycles through while nobody has answered yet.
const DEMO = [
  [3, 2, 1, 2, 1, 3, 2],
  [1, 3, 2, 1, 3, 2, 1],
  [2, 1, 3, 3, 2, 1, 3],
];

// Seven-axis readiness map. `values` holds 0-3 points per question, or null/undefined while unanswered.
export default function Radar({
  values = [],
  active = -1,
  labels = true,
  demo = false,
  className = "",
  questions = SCORECARDS.general.questions,
}) {
  const reduce = useReducedMotion();
  const filled = questions.map((_, i) => values[i] ?? null);
  const d = path(filled);
  const demoPaths = DEMO.map(path);
  return (
    <svg
      viewBox="0 0 360 360"
      className={className}
      role="img"
      aria-label="Readiness map across seven areas"
    >
      {[1, 2, 3].map((level) => (
        <path
          key={level}
          d={ring(level)}
          fill="none"
          stroke="currentColor"
          strokeOpacity={level === 3 ? 0.22 : 0.1}
          strokeWidth="1"
        />
      ))}
      {questions.map((_, i) => {
        const [x, y] = point(i, R);
        return (
          <line
            key={i}
            x1={C}
            y1={C}
            x2={x}
            y2={y}
            stroke="currentColor"
            strokeOpacity={active === i ? 0.5 : 0.1}
            strokeWidth="1"
          />
        );
      })}
      {demo && !reduce ? (
        <motion.path
          fill="currentColor"
          fillOpacity="0.14"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinejoin="round"
          initial={{ d: demoPaths[0] }}
          animate={{ d: [...demoPaths, demoPaths[0]] }}
          transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
        />
      ) : (
        <motion.path
          fill="currentColor"
          fillOpacity="0.16"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinejoin="round"
          initial={false}
          animate={{ d: demo ? demoPaths[0] : d }}
          transition={{ duration: reduce ? 0 : 0.7, ease: [0.15, 0.9, 0.34, 0.95] }}
        />
      )}
      {!demo &&
        questions.map((_, i) => {
          const [x, y] = point(i, (R * (filled[i] ?? 0)) / 3);
          const answered = filled[i] !== null;
          return (
            <motion.circle
              key={i}
              r={active === i ? 5 : answered ? 3.5 : 2.5}
              fill="currentColor"
              fillOpacity={answered || active === i ? 1 : 0.35}
              initial={false}
              animate={{ cx: x, cy: y }}
              transition={{ duration: reduce ? 0 : 0.7, ease: [0.15, 0.9, 0.34, 0.95] }}
            />
          );
        })}
      {labels &&
        questions.map((q, i) => {
          const [x, y] = point(i, R + 26);
          const anchor = x < C - 8 ? "end" : x > C + 8 ? "start" : "middle";
          return (
            <text
              key={q.area}
              x={x}
              y={y}
              textAnchor={anchor}
              dominantBaseline="middle"
              fill="currentColor"
              fillOpacity={active === i ? 1 : 0.5}
              fontSize="11"
              letterSpacing="0.04em"
              style={{ transition: "fill-opacity .4s" }}
            >
              {q.area}
            </text>
          );
        })}
    </svg>
  );
}
